import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const features = [
    {
      icon: 'mdi:percent-circle-outline',
      title: '90% Commission',
      description: 'Keep 90% of your commission earnings. We believe brokers should maximize their profits from every deal.',
      highlight: true
    },
    {
      icon: 'mdi:calculator',
      title: 'Profit Calculator',
      description: 'Built-in calculator in our app to check your exact profits before closing any deal. No surprises, just earnings.'
    },
    {
      icon: 'mdi:file-document-check-outline',
      title: 'CILs & EOIs Made Easy',
      description: 'Easily apply for Client Information Letters and Expressions of Interest with full tracking of approval progress.'
    },
    {
      icon: 'mdi:home-city-outline',
      title: 'Real-Time Listings',
      description: 'Access up-to-date project listings and new launches from top developers across the region.'
    },
    {
      icon: 'mdi:cash-multiple',
      title: 'Commission Tracking',
      description: 'Full visibility from developer approval to contract attachment and commission payout.'
    },
    {
      icon: 'mdi:bank-transfer',
      title: 'Fast Payments',
      description: 'Send collection requests and receive payments via bank transfer or cheque with instant access to developer bank details.'
    }
  ];

  // Dynamically import all partner logos from the logos folder
  // This automatically includes any image files you add to public/assets/images/logos/
  const [partnerLogos, setPartnerLogos] = useState([]);

  useEffect(() => {
    // Fetch the list of logo files from the server
    const fetchLogos = async () => {
      try {
        // We'll use a static list approach that can be easily updated
        // by reading from a manifest file
        const response = await fetch('/assets/images/logos/manifest.json');
        if (response.ok) {
          const logos = await response.json();
          setPartnerLogos(logos);
        }
      } catch (error) {
        console.log('Logo manifest not found, using empty list');
        setPartnerLogos([]);
      }
    };
    fetchLogos();
  }, []);

  return (
    <div className="landing-page">
      {/* CSS Styles */}
      <style>{`
        .landing-page {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          overflow-x: hidden;
        }

        /* Navbar */
        .landing-navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          padding: 16px 0;
          transition: all 0.3s ease;
        }
        
        .landing-navbar.scrolled {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
        }

        .nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .nav-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
        }

        .nav-logo img {
          height: 40px;
          width: auto;
        }

        .nav-logo-text {
          font-size: 28px;
          font-weight: 800;
          color: #1a1a2e;
          letter-spacing: -0.5px;
        }

        .nav-logo-text span {
          color: inherit;
        }

        .nav-buttons {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .btn-nav {
          padding: 10px 24px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .btn-login {
          background: transparent;
          color: #1a1a2e;
          border: 2px solid #e0e0e0;
        }

        .btn-login:hover {
          border-color: #487fff;
          color: #487fff;
        }

        .btn-developer {
          background: linear-gradient(135deg, #487fff 0%, #3366ff 100%);
          color: white;
          border: none;
        }

        .btn-developer:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(72, 127, 255, 0.4);
          color: white;
        }

        .btn-store {
          width: 40px;
          height: 40px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #1a1a2e;
          color: white;
          border-radius: 10px;
          font-size: 20px;
          border: none;
        }

        .btn-store:hover {
          transform: translateY(-2px);
          background: #487fff;
          color: white;
        }

        /* Hamburger Menu Button */
        .hamburger-btn {
          display: none;
          width: 40px;
          height: 40px;
          background: transparent;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          cursor: pointer;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          color: #1a1a2e;
          transition: all 0.3s ease;
        }

        .hamburger-btn:hover {
          border-color: #487fff;
          color: #487fff;
        }

        /* Mobile Menu Overlay */
        .mobile-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1001;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
          display: none;
        }

        .mobile-menu-overlay.open {
          opacity: 1;
          pointer-events: auto;
        }

        /* Mobile Menu Sidebar */
        .mobile-menu {
          position: fixed;
          top: 0;
          right: -280px;
          width: 280px;
          height: 100vh;
          background: white;
          z-index: 1002;
          padding: 24px;
          box-shadow: -5px 0 30px rgba(0, 0, 0, 0.2);
          transition: right 0.3s ease;
          overflow-y: auto;
          display: none;
        }

        .mobile-menu.open {
          right: 0;
        }

        .mobile-menu-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e0e0e0;
        }

        .mobile-menu-logo {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mobile-menu-logo img {
          height: 36px;
        }

        .mobile-menu-logo span {
          font-size: 22px;
          font-weight: 800;
          color: #1a1a2e;
        }

        .close-menu-btn {
          width: 36px;
          height: 36px;
          background: #f0f0f0;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          color: #666;
          transition: all 0.3s ease;
        }

        .close-menu-btn:hover {
          background: #e0e0e0;
          color: #1a1a2e;
        }

        .mobile-menu-links {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .mobile-menu-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          border-radius: 12px;
          text-decoration: none;
          color: #1a1a2e;
          font-weight: 600;
          font-size: 15px;
          transition: all 0.3s ease;
          background: #f8f9fa;
        }

        .mobile-menu-link:hover {
          background: #e8f0ff;
          color: #487fff;
        }

        .mobile-menu-link.primary {
          background: linear-gradient(135deg, #487fff 0%, #3366ff 100%);
          color: white;
        }

        .mobile-menu-link.primary:hover {
          box-shadow: 0 8px 25px rgba(72, 127, 255, 0.4);
        }

        .mobile-menu-link .icon {
          font-size: 20px;
        }

        .mobile-menu-divider {
          height: 1px;
          background: #e0e0e0;
          margin: 16px 0;
        }

        .mobile-menu-section-title {
          font-size: 12px;
          font-weight: 600;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 8px;
          padding-left: 4px;
        }

        /* Hero Section */
        .hero-section {
          min-height: 100vh;
          display: flex;
          align-items: center;
          background: linear-gradient(135deg, #f8faff 0%, #e8f0ff 50%, #f0f4ff 100%);
          position: relative;
          overflow: hidden;
          padding-top: 80px;
        }

        .hero-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 80%;
          height: 150%;
          background: radial-gradient(circle, rgba(72, 127, 255, 0.08) 0%, transparent 70%);
          animation: float 20s ease-in-out infinite;
        }

        .hero-section::after {
          content: '';
          position: absolute;
          bottom: -30%;
          left: -10%;
          width: 60%;
          height: 100%;
          background: radial-gradient(circle, rgba(72, 127, 255, 0.05) 0%, transparent 60%);
          animation: float 15s ease-in-out infinite reverse;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(5deg); }
        }

        .hero-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
          position: relative;
          z-index: 1;
        }

        .hero-content {
          opacity: 0;
          transform: translateY(30px);
          animation: fadeInUp 0.8s ease forwards;
          animation-delay: 0.2s;
        }

        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(72, 127, 255, 0.1);
          color: #487fff;
          padding: 8px 16px;
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 24px;
        }

        .hero-title {
          font-size: 56px;
          font-weight: 800;
          line-height: 1.1;
          color: #1a1a2e;
          margin-bottom: 24px;
          letter-spacing: -1px;
        }

        .hero-title span {
          background: linear-gradient(135deg, #487fff 0%, #6b5ce7 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-description {
          font-size: 18px;
          line-height: 1.7;
          color: #666;
          margin-bottom: 40px;
          max-width: 500px;
        }

        .hero-buttons {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .btn-primary-large {
          padding: 16px 32px;
          font-size: 16px;
          font-weight: 600;
          border-radius: 12px;
          background: linear-gradient(135deg, #487fff 0%, #3366ff 100%);
          color: white;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          transition: all 0.3s ease;
          border: none;
        }

        .btn-primary-large:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 35px rgba(72, 127, 255, 0.4);
          color: white;
        }

        .btn-secondary-large {
          padding: 16px 32px;
          font-size: 16px;
          font-weight: 600;
          border-radius: 12px;
          background: white;
          color: #1a1a2e;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          transition: all 0.3s ease;
          border: 2px solid #e0e0e0;
        }

        .btn-secondary-large:hover {
          border-color: #487fff;
          color: #487fff;
        }

        .hero-image {
          opacity: 0;
          transform: translateX(30px);
          animation: fadeInRight 0.8s ease forwards;
          animation-delay: 0.4s;
        }

        @keyframes fadeInRight {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .phone-mockup {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .phone-frame {
          width: 280px;
          height: 580px;
          background: linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%);
          border-radius: 40px;
          padding: 12px;
          box-shadow: 0 50px 100px rgba(0, 0, 0, 0.2);
          animation: floatPhone 6s ease-in-out infinite;
        }

        @keyframes floatPhone {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-15px) rotate(2deg); }
        }

        .phone-screen {
          width: 100%;
          height: 100%;
          background: linear-gradient(180deg, #487fff 0%, #6b5ce7 100%);
          border-radius: 32px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .phone-screen::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="rgba(255,255,255,0.1)"/><circle cx="80" cy="40" r="3" fill="rgba(255,255,255,0.1)"/><circle cx="40" cy="70" r="2" fill="rgba(255,255,255,0.1)"/><circle cx="70" cy="90" r="2" fill="rgba(255,255,255,0.1)"/></svg>');
          background-size: 100px 100px;
        }

        .phone-logo {
          position: relative;
          z-index: 1;
          margin-bottom: 8px;
        }

        .phone-logo img {
          height: 80px;
          width: auto;
        }

        .phone-tagline {
          font-size: 14px;
          opacity: 0.9;
          position: relative;
          z-index: 1;
        }

        /* Stats Section */
        .stats-bar {
          background: white;
          padding: 40px 0;
          border-bottom: 1px solid #f0f0f0;
        }

        .stats-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 40px;
        }

        .stat-item {
          text-align: center;
        }

        .stat-number {
          font-size: 40px;
          font-weight: 800;
          color: #487fff;
          margin-bottom: 8px;
        }

        .stat-label {
          font-size: 14px;
          color: #666;
          font-weight: 500;
        }

        /* Features Section */
        .features-section {
          padding: 100px 0;
          background: white;
        }

        .features-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-badge {
          display: inline-block;
          background: rgba(72, 127, 255, 0.1);
          color: #487fff;
          padding: 8px 20px;
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .section-title {
          font-size: 42px;
          font-weight: 800;
          color: #1a1a2e;
          margin-bottom: 16px;
          letter-spacing: -0.5px;
        }

        .section-description {
          font-size: 18px;
          color: #666;
          max-width: 600px;
          margin: 0 auto;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }

        .feature-card {
          background: #f8faff;
          padding: 32px;
          border-radius: 20px;
          transition: all 0.3s ease;
          border: 1px solid transparent;
        }

        .feature-card:hover {
          transform: translateY(-8px);
          background: white;
          border-color: #e0e8ff;
          box-shadow: 0 20px 50px rgba(72, 127, 255, 0.15);
        }

        .feature-card.highlight {
          background: linear-gradient(135deg, #487fff 0%, #6b5ce7 100%);
          border: none;
        }

        .feature-card.highlight .feature-icon {
          background: rgba(255, 255, 255, 0.2);
        }

        .feature-card.highlight .feature-title {
          color: white;
        }

        .feature-card.highlight .feature-description {
          color: rgba(255, 255, 255, 0.9);
        }

        .feature-card.highlight:hover {
          transform: translateY(-8px);
          background: linear-gradient(135deg, #3d6fe8 0%, #5a4dd6 100%);
          border-color: transparent;
          box-shadow: 0 20px 50px rgba(72, 127, 255, 0.4);
        }

        /* Commission Banner */
        .commission-banner {
          background: linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%);
          padding: 60px 0;
          position: relative;
          overflow: hidden;
        }

        .commission-banner::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="10" cy="10" r="1" fill="rgba(72,127,255,0.3)"/><circle cx="90" cy="30" r="2" fill="rgba(72,127,255,0.2)"/></svg>');
          background-size: 100px 100px;
        }

        .commission-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 60px;
          position: relative;
          z-index: 1;
          flex-wrap: wrap;
        }

        .commission-percentage {
          text-align: center;
        }

        .commission-number {
          font-size: 120px;
          font-weight: 800;
          background: linear-gradient(135deg, #487fff 0%, #6b5ce7 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1;
          letter-spacing: -5px;
        }

        .commission-label {
          font-size: 28px;
          font-weight: 700;
          color: white;
          margin-top: 8px;
        }

        .commission-content {
          max-width: 500px;
        }

        .commission-title {
          font-size: 32px;
          font-weight: 800;
          color: white;
          margin-bottom: 16px;
          line-height: 1.2;
        }

        .commission-description {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.7;
          margin-bottom: 24px;
        }

        .commission-features {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .commission-feature-item {
          display: flex;
          align-items: center;
          gap: 12px;
          color: white;
          font-size: 15px;
          font-weight: 500;
        }

        .commission-feature-icon {
          width: 28px;
          height: 28px;
          background: linear-gradient(135deg, #487fff 0%, #6b5ce7 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          flex-shrink: 0;
        }

        /* Partners Carousel Section */
        .partners-section {
          padding: 80px 0;
          background: #f8faff;
          overflow: hidden;
        }

        .partners-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .partners-header {
          text-align: center;
          margin-bottom: 50px;
        }

        .partners-title {
          font-size: 36px;
          font-weight: 800;
          color: #1a1a2e;
          margin-bottom: 12px;
        }

        .partners-subtitle {
          font-size: 16px;
          color: #666;
        }

        .partners-carousel {
          position: relative;
          width: 100%;
          overflow: hidden;
          mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
          -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
        }

        .partners-track {
          display: flex;
          gap: 50px;
          animation: scrollLogos 60s linear infinite;
          width: max-content;
        }

        .partners-track:hover {
          animation-play-state: paused;
        }

        @keyframes scrollLogos {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .partner-logo {
          flex-shrink: 0;
          width: 280px;
          height: 160px;
          background: white;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 28px;
          box-shadow: 0 6px 25px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .partner-logo:hover {
          transform: scale(1.05);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
        }

        .partner-logo img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }

        .feature-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #487fff 0%, #6b5ce7 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          color: white;
          font-size: 28px;
        }

        .feature-title {
          font-size: 20px;
          font-weight: 700;
          color: #1a1a2e;
          margin-bottom: 12px;
        }

        .feature-description {
          font-size: 15px;
          color: #666;
          line-height: 1.6;
        }

        /* Download Section */
        .download-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%);
          position: relative;
          overflow: hidden;
        }

        .download-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="10" cy="10" r="1" fill="rgba(72,127,255,0.3)"/><circle cx="90" cy="30" r="2" fill="rgba(72,127,255,0.2)"/><circle cx="30" cy="80" r="1.5" fill="rgba(72,127,255,0.25)"/><circle cx="70" cy="60" r="1" fill="rgba(72,127,255,0.3)"/></svg>');
          background-size: 150px 150px;
        }

        .download-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          text-align: center;
          position: relative;
          z-index: 1;
        }

        .download-title {
          font-size: 42px;
          font-weight: 800;
          color: white;
          margin-bottom: 16px;
        }

        .download-description {
          font-size: 18px;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 40px;
          max-width: 500px;
          margin-left: auto;
          margin-right: auto;
        }

        .download-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .store-button {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          background: white;
          color: #1a1a2e;
          padding: 14px 28px;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s ease;
          min-width: 200px;
        }

        .store-button:hover {
          transform: translateY(-4px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
        }

        .store-icon {
          font-size: 32px;
        }

        .store-text {
          text-align: left;
        }

        .store-text small {
          font-size: 11px;
          color: #666;
          display: block;
        }

        .store-text span {
          font-size: 18px;
          font-weight: 700;
        }

        /* About Section */
        .about-section {
          padding: 100px 0;
          background: #f8faff;
        }

        .about-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }

        .about-image {
          position: relative;
        }

        .about-image-main {
          width: 100%;
          height: 400px;
          background: linear-gradient(135deg, #487fff 0%, #6b5ce7 100%);
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }

        .about-image-main::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect x="10" y="10" width="20" height="20" rx="4" fill="rgba(255,255,255,0.1)"/><rect x="70" y="30" width="15" height="15" rx="3" fill="rgba(255,255,255,0.08)"/><rect x="40" y="60" width="25" height="25" rx="5" fill="rgba(255,255,255,0.1)"/></svg>');
          background-size: 150px 150px;
        }

        .about-logo-large {
          position: relative;
          z-index: 1;
        }

        .about-logo-large img {
          height: 150px;
          width: auto;
        }

        .about-content h2 {
          font-size: 38px;
          font-weight: 800;
          color: #1a1a2e;
          margin-bottom: 24px;
          line-height: 1.2;
        }

        .about-content p {
          font-size: 16px;
          color: #666;
          line-height: 1.8;
          margin-bottom: 16px;
        }

        .about-list {
          margin-top: 32px;
        }

        .about-list-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 16px;
        }

        .about-list-icon {
          width: 24px;
          height: 24px;
          background: linear-gradient(135deg, #487fff 0%, #6b5ce7 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 14px;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .about-list-text {
          font-size: 15px;
          color: #444;
          font-weight: 500;
        }

        /* Footer */
        .landing-footer {
          background: #1a1a2e;
          padding: 60px 0 30px;
        }

        .footer-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .footer-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 40px;
          flex-wrap: wrap;
          gap: 40px;
        }

        .footer-brand {
          max-width: 300px;
        }

        .footer-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }

        .footer-logo img {
          height: 40px;
          width: auto;
        }

        .footer-logo-text {
          font-size: 28px;
          font-weight: 800;
          color: white;
        }

        .footer-logo-text span {
          color: inherit;
        }

        .footer-description {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.7;
        }

        .footer-links {
          display: flex;
          gap: 60px;
        }

        .footer-column h4 {
          font-size: 16px;
          font-weight: 700;
          color: white;
          margin-bottom: 20px;
        }

        .footer-column ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .footer-column li {
          margin-bottom: 12px;
        }

        .footer-column a {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.6);
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .footer-column a:hover {
          color: #487fff;
        }

        .footer-social {
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }

        .social-link {
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 18px;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .social-link:hover {
          background: #487fff;
          transform: translateY(-3px);
        }

        .footer-bottom {
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          padding-top: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }

        .footer-copyright {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.5);
        }

        .footer-legal {
          display: flex;
          gap: 24px;
        }

        .footer-legal a {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.5);
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .footer-legal a:hover {
          color: white;
        }

        /* Responsive */
        @media (max-width: 992px) {
          .hero-container {
            grid-template-columns: 1fr;
            text-align: center;
          }

          .hero-content {
            order: 1;
          }

          .hero-image {
            order: 0;
          }

          .hero-title {
            font-size: 42px;
          }

          .hero-description {
            max-width: 100%;
          }

          .hero-buttons {
            justify-content: center;
          }

          .features-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .about-container {
            grid-template-columns: 1fr;
            text-align: center;
          }

          .about-image {
            order: 1;
          }

          .stats-container {
            grid-template-columns: repeat(2, 1fr);
          }

          .commission-container {
            flex-direction: column;
            text-align: center;
            gap: 40px;
          }

          .commission-number {
            font-size: 100px;
          }

          .commission-content {
            text-align: center;
          }

          .commission-features {
            align-items: center;
          }
        }

        @media (max-width: 768px) {
          .nav-container {
            padding: 0 16px;
          }

          .nav-logo img {
            height: 36px;
          }

          .nav-logo-text {
            font-size: 22px;
          }

          .nav-buttons {
            gap: 8px;
          }

          .btn-nav {
            padding: 8px 14px;
            font-size: 12px;
          }

          .btn-store {
            width: 36px;
            height: 36px;
            font-size: 18px;
          }

          .btn-developer {
            padding: 8px 12px;
          }

          .hero-title {
            font-size: 32px;
          }

          .hero-description {
            font-size: 16px;
          }

          .features-grid {
            grid-template-columns: 1fr;
          }

          .section-title {
            font-size: 32px;
          }

          .download-title {
            font-size: 32px;
          }

          .commission-number {
            font-size: 80px;
            letter-spacing: -3px;
          }

          .commission-label {
            font-size: 22px;
          }

          .commission-title {
            font-size: 26px;
          }

          .commission-description {
            font-size: 14px;
          }

          .commission-feature-item {
            font-size: 14px;
          }

          .footer-top {
            flex-direction: column;
          }

          .footer-links {
            flex-direction: column;
            gap: 30px;
          }

          .stats-container {
            grid-template-columns: 1fr 1fr;
            gap: 24px;
          }

          .stat-number {
            font-size: 32px;
          }

          .partners-title {
            font-size: 28px;
          }

          .partner-logo {
            width: 220px;
            height: 130px;
            padding: 20px;
          }

          .partners-track {
            gap: 40px;
          }
        }

        @media (max-width: 480px) {
          .nav-container {
            padding: 0 12px;
          }

          .nav-logo {
            gap: 6px;
          }

          .nav-logo img {
            height: 32px;
          }

          .nav-logo-text {
            display: none;
          }

          .nav-buttons {
            gap: 6px;
          }

          .nav-buttons .btn-login,
          .nav-buttons .btn-developer,
          .nav-buttons .btn-store {
            display: none;
          }

          .hamburger-btn {
            display: flex;
          }

          .mobile-menu-overlay {
            display: block;
          }

          .mobile-menu {
            display: block;
          }

          .hero-buttons {
            flex-direction: column;
          }

          .btn-primary-large,
          .btn-secondary-large {
            width: 100%;
            justify-content: center;
          }

          .phone-frame {
            width: 220px;
            height: 460px;
          }

          .store-button {
            width: 100%;
            justify-content: center;
          }

          .partners-section {
            padding: 50px 0;
          }

          .partners-title {
            font-size: 24px;
          }

          .partners-subtitle {
            font-size: 14px;
          }

          .partner-logo {
            width: 180px;
            height: 110px;
            padding: 16px;
            border-radius: 14px;
          }

          .partners-track {
            gap: 25px;
          }
        }

        @media (max-width: 360px) {
          .nav-logo img {
            height: 28px;
          }

          .hamburger-btn {
            width: 36px;
            height: 36px;
            font-size: 20px;
          }

          .hero-title {
            font-size: 26px;
          }

          .hero-badge {
            font-size: 12px;
            padding: 6px 12px;
          }
        }
      `}</style>

      {/* Navbar */}
      <nav className={`landing-navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-logo">
            <img src="/assets/images/brokerz-logo.jpg" alt="Brokerz" />
            <span className="nav-logo-text">Broker<span>z</span></span>
          </div>
          <div className="nav-buttons">
            <a 
              href="https://apps.apple.com/gb/app/brokerz-deals-made-simple/id6742421398" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-nav btn-store"
              title="Download on App Store"
            >
              <Icon icon="mdi:apple" />
            </a>
            <a 
              href="https://play.google.com/store/apps/details?id=com.brokerz.realestate&hl=en" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-nav btn-store"
              title="Get it on Google Play"
            >
              <Icon icon="mdi:google-play" />
            </a>
            <Link to="/sign-in" className="btn-nav btn-login">
              Developer Sales Login
            </Link>
            <Link to="/developers-register" className="btn-nav btn-developer">
              <Icon icon="mdi:domain" />
              Developer Sign Up
            </Link>
            {/* Hamburger Menu Button (Mobile) */}
            <button 
              className="hamburger-btn"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Icon icon="mdi:menu" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu Sidebar */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <div className="mobile-menu-logo">
            <img src="/assets/images/brokerz-logo.jpg" alt="Brokerz" />
            <span>Brokerz</span>
          </div>
          <button 
            className="close-menu-btn"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <Icon icon="mdi:close" />
          </button>
        </div>
        
        <div className="mobile-menu-links">
          <div className="mobile-menu-section-title">Account</div>
          <Link 
            to="/sign-in" 
            className="mobile-menu-link primary"
            onClick={() => setMobileMenuOpen(false)}
          >
            <Icon icon="mdi:login" className="icon" />
            Developer Sales Login
          </Link>
          <Link 
            to="/developers-register" 
            className="mobile-menu-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            <Icon icon="mdi:domain" className="icon" />
            Developer Sign Up
          </Link>
          
          <div className="mobile-menu-divider" />
          
          <div className="mobile-menu-section-title">Download App</div>
          <a 
            href="https://apps.apple.com/gb/app/brokerz-deals-made-simple/id6742421398" 
            target="_blank" 
            rel="noopener noreferrer"
            className="mobile-menu-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            <Icon icon="mdi:apple" className="icon" />
            Download on App Store
          </a>
          <a 
            href="https://play.google.com/store/apps/details?id=com.brokerz.realestate&hl=en" 
            target="_blank" 
            rel="noopener noreferrer"
            className="mobile-menu-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            <Icon icon="mdi:google-play" className="icon" />
            Get it on Google Play
          </a>
          
          <div className="mobile-menu-divider" />
          
          <div className="mobile-menu-section-title">Contact</div>
          <a 
            href="mailto:support@thebrokerz.com" 
            className="mobile-menu-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            <Icon icon="mdi:email-outline" className="icon" />
            support@thebrokerz.com
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <Icon icon="mdi:rocket-launch" />
              #1 Real Estate Broker Platform
            </div>
            <h1 className="hero-title">
              Deals Made <span>Simple.</span>
              <br />Grow Wealth, Reduce Effort.
            </h1>
            <p className="hero-description">
              The ultimate platform for real estate brokers. Keep <strong>90% of your commission</strong> and 
              use our built-in calculator to know your exact profits. Say goodbye to manual processes!
            </p>
            <div className="hero-buttons">
              <Link to="/sign-in" className="btn-primary-large">
                <Icon icon="mdi:login" />
                Developer Sales Login
              </Link>
              <Link to="/developers-register" className="btn-secondary-large">
                <Icon icon="mdi:domain" />
                Register as Developer
              </Link>
            </div>
          </div>
          <div className="hero-image">
            <div className="phone-mockup">
              <div className="phone-frame">
                <div className="phone-screen">
                  <div className="phone-logo">
                    <img src="/assets/images/brokerz-logo.jpg" alt="Brokerz" />
                  </div>
                  <div className="phone-tagline">Deals Made Simple</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stats-container">
          <div className="stat-item">
            <div className="stat-number">1000+</div>
            <div className="stat-label">Active Brokers</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">500+</div>
            <div className="stat-label">Projects Listed</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">50+</div>
            <div className="stat-label">Developers</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">1B+</div>
            <div className="stat-label">EGP in Deals</div>
          </div>
        </div>
      </div>

      {/* Commission Banner */}
      <section className="commission-banner">
        <div className="commission-container">
          <div className="commission-percentage">
            <div className="commission-number">90%</div>
            <div className="commission-label">Commission</div>
          </div>
          <div className="commission-content">
            <h2 className="commission-title">Keep More of What You Earn</h2>
            <p className="commission-description">
              At Brokerz, we believe brokers should keep the lion's share of their hard-earned commission. 
              That's why we offer an industry-leading 90% commission split.
            </p>
            <div className="commission-features">
              <div className="commission-feature-item">
                <div className="commission-feature-icon">
                  <Icon icon="mdi:check" />
                </div>
                <span>90% of commission goes directly to you</span>
              </div>
              <div className="commission-feature-item">
                <div className="commission-feature-icon">
                  <Icon icon="mdi:calculator" />
                </div>
                <span>Built-in profit calculator in our app</span>
              </div>
              <div className="commission-feature-item">
                <div className="commission-feature-icon">
                  <Icon icon="mdi:eye" />
                </div>
                <span>Know your exact earnings before closing</span>
              </div>
              <div className="commission-feature-item">
                <div className="commission-feature-icon">
                  <Icon icon="mdi:cash-fast" />
                </div>
                <span>Fast and transparent payouts</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <div className="section-header">
            <div className="section-badge">Why Brokerz?</div>
            <h2 className="section-title">Everything You Need to Succeed</h2>
            <p className="section-description">
              Powerful features designed to maximize your earnings and minimize your effort.
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div className={`feature-card ${feature.highlight ? 'highlight' : ''}`} key={index}>
                <div className="feature-icon">
                  <Icon icon={feature.icon} />
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="about-container">
          <div className="about-image">
            <div className="about-image-main">
              <div className="about-logo-large">
                <img src="/assets/images/brokerz-logo.jpg" alt="Brokerz" />
              </div>
            </div>
          </div>
          <div className="about-content">
            <h2>The Future of Real Estate Brokerage is Here</h2>
            <p>
              Brokerz is revolutionizing how real estate brokers work. Our platform connects brokers 
              with top developers, provides real-time project information, and streamlines the entire 
              deal process from CIL to commission payout.
            </p>
            <p>
              Founded with a mission to empower brokers, we've built a comprehensive ecosystem that 
              handles all the paperwork while you focus on what you do best—closing deals.
            </p>
            <div className="about-list">
              <div className="about-list-item">
                <div className="about-list-icon">
                  <Icon icon="mdi:check" />
                </div>
                <span className="about-list-text">Full visibility into approval processes</span>
              </div>
              <div className="about-list-item">
                <div className="about-list-icon">
                  <Icon icon="mdi:check" />
                </div>
                <span className="about-list-text">Instant access to developer bank details</span>
              </div>
              <div className="about-list-item">
                <div className="about-list-icon">
                  <Icon icon="mdi:check" />
                </div>
                <span className="about-list-text">Daily updated inventory files</span>
              </div>
              <div className="about-list-item">
                <div className="about-list-icon">
                  <Icon icon="mdi:check" />
                </div>
                <span className="about-list-text">Commission tracking from start to payout</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section - Only show if there are logos */}
      {partnerLogos.length > 0 && (
        <section className="partners-section">
          <div className="partners-container">
            <div className="partners-header">
              <h2 className="partners-title">Our Partners</h2>
              <p className="partners-subtitle">Trusted by leading developers across Egypt</p>
            </div>
          </div>
          <div className="partners-carousel">
            <div className="partners-track" style={{ animationDuration: `${Math.max(60, partnerLogos.length * 1.5)}s` }}>
              {/* First set of logos */}
              {partnerLogos.map((logo, index) => (
                <div className="partner-logo" key={`logo-1-${index}`}>
                  <img src={`/assets/images/logos/${logo}`} alt={`Partner ${index + 1}`} loading="lazy" />
                </div>
              ))}
              {/* Duplicate for seamless loop */}
              {partnerLogos.map((logo, index) => (
                <div className="partner-logo" key={`logo-2-${index}`}>
                  <img src={`/assets/images/logos/${logo}`} alt={`Partner ${index + 1}`} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Download Section */}
      <section className="download-section">
        <div className="download-container">
          <h2 className="download-title">Download the App</h2>
          <p className="download-description">
            Get Brokerz on your phone and manage your deals on the go. Available on iOS and Android.
          </p>
          <div className="download-buttons">
            <a 
              href="https://apps.apple.com/gb/app/brokerz-deals-made-simple/id6742421398" 
              target="_blank" 
              rel="noopener noreferrer"
              className="store-button"
            >
              <Icon icon="mdi:apple" className="store-icon" />
              <div className="store-text">
                <small>Download on the</small>
                <span>App Store</span>
              </div>
            </a>
            <a 
              href="https://play.google.com/store/apps/details?id=com.brokerz.realestate&hl=en" 
              target="_blank" 
              rel="noopener noreferrer"
              className="store-button"
            >
              <Icon icon="mdi:google-play" className="store-icon" />
              <div className="store-text">
                <small>Get it on</small>
                <span>Google Play</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-top">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/assets/images/brokerz-logo.jpg" alt="Brokerz" />
                <span className="footer-logo-text">Broker<span>z</span></span>
              </div>
              <p className="footer-description">
                The ultimate platform for real estate brokers to streamline deal-making and boost earnings. 
                Maximize your wealth with Brokerz!
              </p>
              <div className="footer-social">
                <a href="https://apps.apple.com/gb/app/brokerz-deals-made-simple/id6742421398" target="_blank" rel="noopener noreferrer" className="social-link">
                  <Icon icon="mdi:apple" />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.brokerz.realestate&hl=en" target="_blank" rel="noopener noreferrer" className="social-link">
                  <Icon icon="mdi:google-play" />
                </a>
              </div>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Platform</h4>
                <ul>
                  <li><Link to="/sign-in">Developer Sales Login</Link></li>
                  <li><Link to="/developers-register">Developer Registration</Link></li>
                  <li><Link to="/forgot-password">Reset Password</Link></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact</h4>
                <ul>
                  <li><a href="mailto:support@thebrokerz.com">support@thebrokerz.com</a></li>
                  <li><span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Cairo, Egypt</span></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">
              © {new Date().getFullYear()} The Brokers LLC. All rights reserved.
            </p>
            <div className="footer-legal">
              <a href="https://thebrokerz.com/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
